/** Frontend wrapper for /bot-memory: notes workspace bots keep between tasks.
 * Personal notes (scope `me`) are private to a member but visible to workspace
 * admins; workspace notes are written by admins and read by every bot. Note
 * content is model-written text: render it as plain text only. */
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';

export type BotMemoryScope = 'workspace' | 'me' | `user:${string}`;

export interface ScopeSummary {
  exists: boolean;
  count: number;
  bytes: number;
  oversized: boolean;
  lastChangedAt: string | null;
  clearPending: boolean;
}
export interface MemberSummary extends ScopeSummary { userId: string; name: string }
export interface BotMemorySummary {
  enabled: boolean;
  /** A real owner or admin role in this workspace. Gates the per-member notes. */
  isAdmin: boolean;
  /** Admins and internal staff. Missing on an older server, where it equals isAdmin. */
  canEditWorkspace: boolean;
  me: ScopeSummary;
  workspace: ScopeSummary;
  /** Admins only. Can lag until a member's next task finishes. */
  members: MemberSummary[];
}

export interface MemoryItem { id: string; path: string; sizeBytes: number; sha256: string; updatedAt: string }
export interface MemoryContent extends MemoryItem { content: string; writtenByTaskId?: string }
export interface MemoryList { memories: MemoryItem[]; truncated: boolean; clearPending: boolean }
export interface ClearResult { status: 'cleared' | 'deferred' }
export interface DeleteResult { redactionComplete: boolean }

export class BotMemoryError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'BotMemoryError';
  }
}

const BASE = `${API_BASE}/bot-memory`;

export const emptyScope = (): ScopeSummary => ({ exists: false, count: 0, bytes: 0, oversized: false, lastChangedAt: null, clearPending: false });

function scopeSummary(raw: Partial<ScopeSummary> | null | undefined): ScopeSummary {
  return {
    exists: raw?.exists === true,
    count: typeof raw?.count === 'number' ? raw.count : 0,
    bytes: typeof raw?.bytes === 'number' ? raw.bytes : 0,
    oversized: raw?.oversized === true,
    lastChangedAt: typeof raw?.lastChangedAt === 'string' ? raw.lastChangedAt : null,
    clearPending: raw?.clearPending === true,
  };
}

async function request<T>(workspaceId: string, path: string, init: RequestInit = {}): Promise<T> {
  const query = new URLSearchParams({ workspaceId });
  const res = await fetch(`${BASE}${path}?${query}`, { ...init, headers: await getAuthHeaders({ 'Content-Type': 'application/json' }) });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const data = await res.json();
      message = (data && (data.error || data.message)) || message;
      code = data && typeof data.code === 'string' ? data.code : undefined;
    } catch { /* keep the status text */ }
    throw new BotMemoryError(message, res.status, code);
  }
  return res.json() as Promise<T>;
}

const scopePath = (scope: BotMemoryScope) => `/${encodeURIComponent(scope)}`;

export const botMemoryService = {
  async summary(workspaceId: string): Promise<BotMemorySummary> {
    const data = await request<Partial<BotMemorySummary> | null>(workspaceId, '');
    return {
      enabled: data?.enabled === true,
      isAdmin: data?.isAdmin === true,
      canEditWorkspace: typeof data?.canEditWorkspace === 'boolean' ? data.canEditWorkspace : data?.isAdmin === true,
      me: scopeSummary(data?.me),
      workspace: scopeSummary(data?.workspace),
      members: Array.isArray(data?.members) ? data.members.map((m) => ({ ...scopeSummary(m), userId: m.userId, name: m.name })) : [],
    };
  },
  async list(workspaceId: string, scope: BotMemoryScope): Promise<MemoryList> {
    const data = await request<Partial<MemoryList> | null>(workspaceId, `${scopePath(scope)}/memories`);
    return { memories: Array.isArray(data?.memories) ? data.memories : [], truncated: data?.truncated === true, clearPending: data?.clearPending === true };
  },
  async read(workspaceId: string, scope: BotMemoryScope, memoryId: string): Promise<MemoryContent> {
    return (await request<{ memory: MemoryContent }>(workspaceId, `${scopePath(scope)}/memories/${encodeURIComponent(memoryId)}`)).memory;
  },
  /** Without `sha` this creates; with it, it updates only if the note is unchanged. */
  async save(workspaceId: string, input: { path: string; content: string; sha?: string }): Promise<MemoryContent> {
    return (await request<{ memory: MemoryContent }>(workspaceId, '/workspace/memories', { method: 'PUT', body: JSON.stringify(input) })).memory;
  },
  async remove(workspaceId: string, scope: BotMemoryScope, memoryId: string): Promise<DeleteResult> {
    const data = await request<{ redactionComplete?: boolean } | null>(workspaceId, `${scopePath(scope)}/memories/${encodeURIComponent(memoryId)}`, { method: 'DELETE' });
    return { redactionComplete: data?.redactionComplete !== false };
  },
  async clear(workspaceId: string, scope: BotMemoryScope): Promise<ClearResult> {
    const data = await request<{ status?: string } | null>(workspaceId, scopePath(scope), { method: 'DELETE' });
    return { status: data?.status === 'deferred' ? 'deferred' : 'cleared' };
  },
};
